import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeftRounded, ChevronRightRounded } from "@mui/icons-material";

/**
 * A single horizontal row that scrolls, for rows that can legitimately be longer
 * than the screen — a filter strip, a KPI band. Three ways to move it, because the
 * same screen is used with a finger, a mouse and a trackpad:
 *
 *   - drag / swipe, which the browser gives us,
 *   - the mouse wheel while the pointer is over the row,
 *   - the arrow buttons, for the mouse that has no horizontal wheel.
 *
 * The scrollbar is hidden on purpose: the global rule in styles/Style.scss makes
 * every scrollbar 10px and primary-coloured, which under a 30px chip reads as a
 * second piece of UI rather than as a hint.
 *
 * Three nested elements, each load-bearing:
 *   wrapper  — `relative`, the anchor the arrows are positioned against.
 *   scroller — the only element with overflow. The arrows are NOT inside it, or
 *              they would be clipped by it and would scroll away with the row.
 *   track    — `w-max`, so it sizes to its content. Without that the flex row
 *              shrinks to the scroller and nothing ever overflows.
 */
const ScrollRow = ({
  children,
  className = "",
  gapClass = "gap-[6px]",
  prevLabel,
  nextLabel,
}) => {
  const scroller = useRef(null);
  const track = useRef(null);
  // Where the last arrow click was aiming, so a second click mid-animation
  // continues from there. Null once the row has caught up or the user took over.
  const target = useRef(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const measure = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const start = el.scrollLeft > 1;
    const end = max > 1 && el.scrollLeft < max - 1;

    if (target.current != null && Math.abs(el.scrollLeft - target.current) < 2)
      target.current = null;

    // Returning `prev` unchanged is what stops a scroll gesture re-rendering every
    // item in the row: `scroll` fires ~60 times a second, and a fresh object each
    // time would re-render on all of them.
    setEdges((prev) =>
      prev.start === start && prev.end === end ? prev : { start, end }
    );
  }, []);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;

    measure();
    el.addEventListener("scroll", measure, { passive: true });

    if (typeof ResizeObserver === "undefined")
      return () => el.removeEventListener("scroll", measure);

    // Both nodes, and the TRACK is the one that matters: items arrive from a fetch
    // after mount, which changes scrollWidth without ever resizing the scroller, so
    // watching the scroller alone would leave the arrows hidden until something
    // else happened to resize. The track also catches a webfont landing and an
    // en→ar switch making every label longer.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (track.current) ro.observe(track.current);

    return () => {
      el.removeEventListener("scroll", measure);
      ro.disconnect();
    };
  }, [measure]);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;

    const onWheel = (e) => {
      // A trackpad's horizontal swipe already scrolls this row natively; taking it
      // as well would move it twice per gesture.
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;

      // deltaMode: 0 = pixels, 1 = lines (Firefox's default), 2 = pages. Without
      // this a Firefox notch moves the row three pixels and reads as broken.
      const dy =
        e.deltaMode === 1
          ? e.deltaY * 16
          : e.deltaMode === 2
            ? e.deltaY * el.clientWidth
            : e.deltaY;

      const max = el.scrollWidth - el.clientWidth;
      if (max <= 1) return;

      // At an end and still pushing that way, let the event through rather than
      // swallowing it, so whatever is below still scrolls when the pointer happens
      // to be resting over the row. A strip that traps the wheel feels broken.
      if ((dy < 0 && el.scrollLeft <= 0) || (dy > 0 && el.scrollLeft >= max - 1))
        return;

      e.preventDefault();
      // Assigned directly rather than scrollBy({behavior:"smooth"}): a wheel fires
      // ~60 times a second and queued smooth animations fight into a stutter.
      el.scrollLeft = Math.max(0, Math.min(max, el.scrollLeft + dy));
      target.current = null;
    };

    // React 18 registers its wheel listeners as PASSIVE, so preventDefault inside
    // an onWheel prop is a no-op. Same reason utils/numericInput.js binds its own.
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const step = useCallback((dir) => {
    const el = scroller.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    // From the last INTENDED position, not the animating one: two fast clicks
    // would otherwise both measure from part-way through the first animation and
    // travel about one and a third pages instead of two.
    const base = target.current == null ? el.scrollLeft : target.current;
    const amount = Math.max(80, Math.round(el.clientWidth * 0.8));
    const next = Math.max(0, Math.min(max, base + dir * amount));
    target.current = next;
    el.scrollTo({
      left: next,
      behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  }, []);

  // 26px square, which is the height of a compact FilterChip and sits inside a
  // full-size one. Absolutely positioned, so showing an arrow can never make the
  // row taller than its content already makes it.
  const arrow =
    "absolute top-1/2 z-[2] flex h-[26px] w-[26px] -translate-y-1/2 items-center " +
    "justify-center rounded-full border border-gray-200 bg-white text-slate-500 " +
    "shadow-[rgba(99,99,99,0.2)_0px_2px_8px_0px] transition-colors " +
    "hover:text-primary cursor-pointer";
  // to-white/0 rather than to-transparent: `transparent` is transparent BLACK, and
  // some engines grey the midpoint of a gradient that interpolates towards it.
  const fade = "pointer-events-none absolute inset-y-0 z-[1] w-[40px]";

  return (
    <div className={`relative ${className}`}>
      <div
        ref={scroller}
        className="overflow-x-auto [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: "none" }}
      >
        <div ref={track} className={`flex w-max ${gapClass}`}>
          {children}
        </div>
      </div>

      {edges.start && (
        <>
          <div
            aria-hidden="true"
            className={`${fade} start-0 bg-gradient-to-r from-white via-white to-white/0`}
          />
          <button
            type="button"
            onClick={() => step(-1)}
            aria-label={prevLabel}
            title={prevLabel}
            className={`${arrow} start-0`}
          >
            <ChevronLeftRounded className="!text-[18px]" />
          </button>
        </>
      )}

      {edges.end && (
        <>
          <div
            aria-hidden="true"
            className={`${fade} end-0 bg-gradient-to-l from-white via-white to-white/0`}
          />
          <button
            type="button"
            onClick={() => step(1)}
            aria-label={nextLabel}
            title={nextLabel}
            className={`${arrow} end-0`}
          >
            <ChevronRightRounded className="!text-[18px]" />
          </button>
        </>
      )}
    </div>
  );
};

export default ScrollRow;
